import { FC, useEffect, useMemo } from 'react';

import { useSearchParams } from 'react-router-dom';

import usePlace from 'providers/Place/hooks';
import { LocationAnalyticsType } from 'types/Place';

type AnalyticsScriptsType = {};

const ConsentMode: React.FC<{ id: string; granted?: boolean }> = ({ granted, id }) => {
	return (
		<script
			id={id}
			dangerouslySetInnerHTML={{
				__html: `
					window.dataLayer = window.dataLayer || [];
					function gtag(){window.dataLayer.push(arguments);}
					gtag('consent', 'default', {
						'ad_storage': '${granted ? 'granted' : 'denied'}',
						'ad_user_data': '${granted ? 'granted' : 'denied'}',
						'ad_personalization': '${granted ? 'granted' : 'denied'}',
						'analytics_storage': '${granted ? 'granted' : 'denied'}',
						'wait_for_update': 500,
					});
					window.acceptGoogleAnalyticsConsentCookies = () => {
						gtag('consent', 'update', {
							'ad_user_data': 'granted',
							'ad_personalization': 'granted',
							'ad_storage': 'granted',
							'analytics_storage': 'granted'
						});
					};
				`,
			}}
		/>
	);
};

const AnalyticsScripts: FC<AnalyticsScriptsType> = () => {
	const { place } = usePlace();
	const [searchParams] = useSearchParams();

	const analytics = place?.marketing?.analytics;
	const isCookiesGranted = searchParams.get('cookies-granted') === 'true';

	useEffect(() => {
		window.isCookiesGranted = isCookiesGranted;
		window.analytics = {
			isGa: !!analytics?.ga,
			isGtm: !!analytics?.gtm,
			isFbPixel: !!analytics?.fbPixel,
		};
	}, [isCookiesGranted, analytics]);

	return (
		<>
			{analytics?.fbPixel && isCookiesGranted && (
				<script
					id={'analytics-fbp'}
					dangerouslySetInnerHTML={{
						__html: `
							!function(f,b,e,v,n,t,s)
							{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
							n.callMethod.apply(n,arguments):n.queue.push(arguments)};
							if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version="2.0";
							n.queue=[];t=b.createElement(e);t.async=!0;
							t.src=v;s=b.getElementsByTagName(e)[0];
							s.parentNode.insertBefore(t,s)}(window, document,"script",
							"https://connect.facebook.net/en_US/fbevents.js");
							fbq("init", "${analytics?.fbPixel}");
							fbq("track", "PageView");
						`,
					}}
				/>
			)}
			{analytics?.ga && (
				<>
					{!analytics?.gtm && (
						<ConsentMode id={'analytics-ga-consent-mode'} granted={isCookiesGranted} />
					)}
					<script async src={`https://www.googletagmanager.com/gtag/js?id=${analytics.ga}`} />
					<script
						id="analytics-ga"
						dangerouslySetInnerHTML={{
							__html: `
								window.dataLayer = window.dataLayer || [];
								function gtag(){window.dataLayer.push(arguments);}
								gtag('js', new Date());
								gtag('config', '${analytics?.ga}');
							`,
						}}
					/>
				</>
			)}
			{analytics?.gtm && (
				<>
					<ConsentMode id={'analytics-gtm-consent-mode'} granted={isCookiesGranted} />
					<script
						id="analytics-gtm"
						dangerouslySetInnerHTML={{
							__html: `
							(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
							new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
							j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
							'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
							})(window,document,'script','dataLayer','${analytics?.gtm}');
						`,
						}}
					/>
				</>
			)}
		</>
	);
};

export default AnalyticsScripts;
